import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { Wrench } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import { PleriClient } from '../../../../client/PleriClient';
import {
  useEnabledTicketingTools,
  type EnabledTicketingTool,
} from '../../../../hooks/useEnabledTicketingTools';
import type { ContextItem } from '../../../../store/chatStore';
import type {
  VulnerabilityAsset,
  VulnerabilityDefinition,
  VulnerabilityDetails,
  VulnerabilityEvaluationStatus,
  VulnerabilityInvestigationData,
  VulnerabilityInvestigationEvaluation,
  VulnerabilityNextBestAction,
  VulnerabilityPatchRemediationDetails,
  VulnerabilityRemediationPackage,
  VulnerabilityInvestigationTag,
} from './types';
import './VulnerabilityInvestigation.css';
import type { TFunction } from 'i18next';

import { useTranslation, Trans } from 'react-i18next';
import { formatLocale } from '@plerionhq/design-system';

interface VulnerabilityInvestigationProps {
  data: VulnerabilityInvestigationData;
  className?: string;
}

interface DetailRow {
  label: string;
  value: string;
  href?: string;
  linkLabel?: string;
}

interface AssetTagRow {
  key: string | undefined;
  value: string | undefined;
}

interface ActionField {
  label: string;
  value: string;
}

interface ActionPackageRow {
  packageName: string;
  installedVersion?: string;
  fixedVersion?: string;
  target?: string;
}

interface ActionPackageGroups {
  fixablePackages: ActionPackageRow[];
  unfixablePackages: ActionPackageRow[];
}

type EvaluationFilter = 'ALL' | 'NOT_MET' | 'MET' | 'UNKNOWN';
type NormalizedStatus = 'MET' | 'NOT_MET' | 'UNKNOWN';

const NEXT_BEST_ACTION_RESERVED_KEYS = new Set([
  'type',
  'title',
  'description',
  'summary',
  'priority',
  'scope',
  'impact',
  'highlights',
  'details',
  'references',
]);

const DISPLAYABLE_NEXT_BEST_ACTION_TYPES = new Set([
  'HARDEN',
  'PATCH',
  'MITIGATE',
  'EXEMPT',
]);

const STATUS_TAG_LABELS: Record<NormalizedStatus, string> = {
  MET: 'MET',
  NOT_MET: 'NOT MET',
  UNKNOWN: 'UNKNOWN',
};

const VERDICT_LABELS: Record<string, string> = {
  EXPLOITABLE: 'Exploitable',
  LIKELY_EXPLOITABLE: 'Likely exploitable',
  NOT_EXPLOITABLE: 'Not exploitable',
  LIKELY_NOT_EXPLOITABLE: 'Likely not exploitable',
  UNKNOWN: 'Unknown',
  CONDITIONAL: 'Conditional',
};

const STATUS_CLASS: Record<NormalizedStatus, string> = {
  MET: 'status-met',
  NOT_MET: 'status-not-met',
  UNKNOWN: 'status-unknown',
};

const FALLBACK_DESCRIPTION =
  'Evidence is currently insufficient to summarize this requirement.';

const VERDICT_TOOLTIPS: Record<string, string> = {
  NOT_EXPLOITABLE:
    'This vulnerability cannot be exploited on this asset because a required condition is definitively not met.',
  LIKELY_NOT_EXPLOITABLE:
    'This vulnerability is unlikely to be exploitable on this asset because one or more conditions are not met, though we cannot definitively rule out exploitation.',
  EXPLOITABLE:
    'This vulnerability can be exploited on this asset because all required conditions are met with high confidence.',
  LIKELY_EXPLOITABLE:
    'This vulnerability is likely exploitable on this asset because all required conditions appear to be met, though some evidence is uncertain.',
  UNKNOWN:
    'I do not have enough information to determine whether this vulnerability can be exploited on this asset.',
};

function pickFirst<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const value of values) {
    if (
      value !== null &&
      value !== undefined &&
      !(typeof value === 'string' && value.trim() === '')
    ) {
      return value;
    }
  }
  return undefined;
}

function normalizeStatus(
  rawStatus?: VulnerabilityEvaluationStatus,
): NormalizedStatus {
  if (!rawStatus || typeof rawStatus !== 'string') {
    return 'UNKNOWN';
  }
  const normalized = rawStatus.replace(/-/g, '_').toUpperCase();
  if (normalized === 'PASS') {
    return 'MET';
  }
  if (normalized === 'FAIL') {
    return 'NOT_MET';
  }
  if (
    normalized === 'MET' ||
    normalized === 'NOT_MET' ||
    normalized === 'UNKNOWN'
  ) {
    return normalized;
  }
  return 'UNKNOWN';
}

function normalizeText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized || undefined;
}

function stripTrailingPunctuation(value: string): string {
  return value.replace(/[.!?]+$/g, '').trim();
}

function isGenericPlaceholderReasoning(value: string): boolean {
  return /detailed vulnerability metadata is not available in this payload yet/i.test(
    value,
  );
}

function buildEvaluationReasoning(
  evaluation: VulnerabilityInvestigationEvaluation,
): string {
  const requirement = evaluation.requirement || {};
  const directReasoning = normalizeText(
    pickFirst(
      evaluation.reasoning,
      requirement.requirementReasoning,
      requirement.reasoning,
    ),
  );
  if (directReasoning && !isGenericPlaceholderReasoning(directReasoning)) {
    return directReasoning;
  }

  const requirementEvidence = normalizeText(
    pickFirst(requirement.requirementEvidence, requirement.evidence),
  );
  const evidenceEntries = Array.isArray(evaluation.evidence)
    ? evaluation.evidence
        .map((entry) => normalizeText(entry))
        .filter((entry): entry is string => Boolean(entry))
    : [];
  const evidenceSummary = evidenceEntries[0];
  const status = normalizeStatus(evaluation.status);
  const statusPhrase =
    status === 'MET' ? 'met' : status === 'NOT_MET' ? 'not met' : 'unknown';

  if (requirementEvidence && evidenceSummary) {
    return `Based on requirement evidence (${stripTrailingPunctuation(
      requirementEvidence,
    )}) and observed context (${stripTrailingPunctuation(
      evidenceSummary,
    )}), this condition is ${statusPhrase}.`;
  }
  if (requirementEvidence) {
    return `Based on requirement evidence (${stripTrailingPunctuation(
      requirementEvidence,
    )}), this condition is ${statusPhrase}.`;
  }
  if (evidenceSummary) {
    return `Based on observed context (${stripTrailingPunctuation(
      evidenceSummary,
    )}), this condition is ${statusPhrase}.`;
  }

  return FALLBACK_DESCRIPTION;
}

function normalizeVerdict(rawVerdict?: string): string {
  if (!rawVerdict || typeof rawVerdict !== 'string') {
    return 'UNKNOWN';
  }
  return rawVerdict
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
}

function toTitle(text: string | undefined | null): string {
  if (!text || typeof text !== 'string') {
    return 'Unknown';
  }
  return text
    .toLowerCase()
    .split(/[\s_]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatActionFieldLabel(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b(cve|ami|aws|ec2|ip|id|roi|url)\b/gi, (match) =>
      match.toUpperCase(),
    )
    .split(' ')
    .map((part) => {
      if (part === part.toUpperCase()) {
        return part;
      }
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
}

function formatDate(dateLike: string | undefined): string {
  if (!dateLike) {
    return 'Unknown';
  }
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) {
    return String(dateLike);
  }
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat(formatLocale(), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(date);
}

function parseScore(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
}

function scoreToSeverity(score: number | null): string {
  if (score === null || score === undefined) {
    return 'Unknown';
  }
  if (score >= 9) {
    return 'Critical';
  }
  if (score >= 7) {
    return 'High';
  }
  if (score >= 4) {
    return 'Medium';
  }
  if (score > 0) {
    return 'Low';
  }
  return 'None';
}

function severityLevelToScore(
  severityLevel: string | undefined,
): number | null {
  if (!severityLevel) {
    return null;
  }
  const level = String(severityLevel).toUpperCase();
  if (level === 'CRITICAL') {
    return 9.0;
  }
  if (level === 'HIGH') {
    return 7.5;
  }
  if (level === 'MEDIUM') {
    return 5.5;
  }
  if (level === 'LOW') {
    return 2.5;
  }
  return null;
}

function severityLabelToScore(level: string | undefined): number | null {
  if (!level) {
    return null;
  }
  const normalized = level.trim().toUpperCase();
  if (normalized === 'NONE') {
    return 0;
  }
  return severityLevelToScore(normalized);
}

function verdictToSeverity(
  verdict: string | undefined,
  fallbackScore: number | null,
): string {
  const normalizedVerdict = normalizeVerdict(verdict);
  if (normalizedVerdict === 'NOT_EXPLOITABLE') {
    return 'Low';
  }
  if (normalizedVerdict === 'LIKELY_NOT_EXPLOITABLE') {
    return 'Low';
  }
  if (
    normalizedVerdict === 'EXPLOITABLE' ||
    normalizedVerdict === 'LIKELY_EXPLOITABLE'
  ) {
    return scoreToSeverity(fallbackScore);
  }
  if (normalizedVerdict === 'CONDITIONAL') {
    return scoreToSeverity(fallbackScore);
  }
  return 'Undetermined';
}

function severityTone(severity: string | undefined): string {
  const value = (severity || '').toUpperCase();
  if (value === 'CRITICAL') {
    return 'tone-critical';
  }
  if (value === 'HIGH') {
    return 'tone-high';
  }
  if (value === 'MEDIUM') {
    return 'tone-medium';
  }
  if (value === 'LOW') {
    return 'tone-low';
  }
  if (value === 'NONE') {
    return 'tone-none';
  }
  return 'tone-unknown';
}

function formatPercent(value: unknown, digits = 0): string {
  if (value === null || value === undefined || value === '') {
    return 'Unknown';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }
  const percentage = numeric <= 1 ? numeric * 100 : numeric;
  return `${percentage.toFixed(digits)}%`;
}

function formatScore(value: number | null, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return value.toFixed(digits);
}

function scoreToProgressPercent(value: number | null): number {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 0;
  }
  const scaleMax = value <= 3 ? 3 : 10;
  return Math.max(0, Math.min((value / scaleMax) * 100, 100));
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return 'Unknown';
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'None';
    }
    return value
      .map((entry) => {
        if (typeof entry === 'string' || typeof entry === 'number') {
          return String(entry);
        }
        try {
          return JSON.stringify(entry);
        } catch {
          return String(entry);
        }
      })
      .join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[object]';
    }
  }
  return String(value);
}

function formatDisplayValue(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const formatted = formatValue(value);
  return formatted === 'Unknown' ? undefined : formatted;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeActionType(rawType?: string): string {
  if (!rawType || typeof rawType !== 'string') {
    return 'OTHER';
  }
  return rawType
    .trim()
    .replace(/[\s-]+/g, '_')
    .toUpperCase();
}

function parseActionPriority(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const match = value.match(/\d+/);
  if (!match) {
    return null;
  }
  const numeric = Number(match[0]);
  return Number.isFinite(numeric) ? numeric : null;
}

function buildActionFields(fields: unknown): ActionField[] {
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields
    .map((field) => {
      if (!isRecord(field)) {
        return null;
      }
      const label = normalizeText(field.label);
      const value = formatActionFieldValue(field.value);
      if (!label || !value) {
        return null;
      }
      return { label, value };
    })
    .filter((field): field is ActionField => field !== null);
}

function formatPackageActionValue(
  value: Record<string, unknown>,
): string | undefined {
  const packageName = normalizeText(
    pickFirst(value.packageName, value.name, value.package),
  );
  const installedVersion = normalizeText(value.installedVersion);
  const fixedVersion = normalizeText(value.fixedVersion);
  const packagePath = normalizeText(value.packagePath);

  const packageSummary = packageName
    ? installedVersion && fixedVersion
      ? `${packageName} ${installedVersion} -> ${fixedVersion}`
      : installedVersion
        ? `${packageName} ${installedVersion}`
        : packageName
    : undefined;

  const location = packagePath;

  if (!packageSummary && !location) {
    return undefined;
  }

  if (!packageSummary) {
    return location;
  }

  return location ? `${packageSummary} (${location})` : packageSummary;
}

function formatActionFieldValue(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const entries = value
      .map((entry) => formatActionFieldValue(entry))
      .filter((entry): entry is string => Boolean(entry));

    return entries.length > 0 ? entries.join('; ') : undefined;
  }

  if (isRecord(value)) {
    return formatPackageActionValue(value);
  }

  return formatDisplayValue(value);
}

function buildActionObjectFields(
  fields: unknown,
  options?: { omitKeys?: string[] },
): ActionField[] {
  if (!isRecord(fields)) {
    return [];
  }

  const omittedKeys = new Set(options?.omitKeys || []);

  return Object.entries(fields)
    .filter(([key]) => !omittedKeys.has(key))
    .map(([key, value]) => {
      const formattedValue = formatActionFieldValue(value);
      if (!formattedValue) {
        return null;
      }

      return {
        label: formatActionFieldLabel(key),
        value: formattedValue,
      };
    })
    .filter((field): field is ActionField => field !== null);
}

function buildFallbackActionDetails(
  action: VulnerabilityNextBestAction,
): ActionField[] {
  return Object.entries(action)
    .filter(([key]) => !NEXT_BEST_ACTION_RESERVED_KEYS.has(key))
    .map(([key, value]) => {
      const formattedValue = formatActionFieldValue(value);
      if (!formattedValue) {
        return null;
      }
      return {
        label: formatActionFieldLabel(key),
        value: formattedValue,
      };
    })
    .filter((field): field is ActionField => field !== null);
}

function getActionNarrative(
  action: VulnerabilityNextBestAction,
): string | undefined {
  return normalizeText(pickFirst(action.description, action.summary));
}

function getActionImpact(
  action: VulnerabilityNextBestAction,
): string | undefined {
  const rawImpact = action.impact as unknown;
  const explicitImpact = normalizeText(rawImpact);
  if (explicitImpact) {
    return explicitImpact;
  }

  if (isRecord(rawImpact)) {
    return normalizeText(
      pickFirst(rawImpact.note, rawImpact.description, rawImpact.summary),
    );
  }

  return undefined;
}

function getActionDetailObject(
  action: VulnerabilityNextBestAction,
): Record<string, unknown> | undefined {
  return isRecord(action.details) ? action.details : undefined;
}

function buildActionDetails(
  action: VulnerabilityNextBestAction,
): ActionField[] {
  const detailObject = getActionDetailObject(action);
  const explicitDetails = [
    ...buildActionFields(action.highlights),
    ...buildActionFields(Array.isArray(action.details) ? action.details : []),
    ...buildActionObjectFields(action.impact as unknown, {
      omitKeys: ['scope', 'note', 'description', 'summary'],
    }),
    ...buildActionObjectFields(detailObject, {
      omitKeys: ['fixablePackages', 'unfixablePackages'],
    }),
  ];

  if (explicitDetails.length > 0) {
    return explicitDetails;
  }

  return buildFallbackActionDetails(action);
}

function normalizeRemediationPackage(value: unknown): ActionPackageRow | null {
  if (!isRecord(value)) {
    return null;
  }

  const packageEntry = value as VulnerabilityRemediationPackage;
  const packageName = normalizeText(
    pickFirst(packageEntry.packageName, value.name, value.package),
  );
  const installedVersion = normalizeText(
    pickFirst(
      packageEntry.installedVersion,
      value.currentVersion,
      value.version,
    ),
  );
  const fixedVersion = normalizeText(
    pickFirst(
      packageEntry.fixedVersion,
      value.targetVersion,
      value.upgradeToVersion,
      value.upgradeVersion,
    ),
  );
  const target = normalizeText(packageEntry.packagePath);

  if (!packageName && !installedVersion && !fixedVersion && !target) {
    return null;
  }

  return {
    packageName: packageName || 'Package',
    installedVersion,
    fixedVersion,
    target: target || undefined,
  };
}

function getPatchPackageGroups(
  action: VulnerabilityNextBestAction,
): ActionPackageGroups | null {
  if (normalizeActionType(action.type) !== 'PATCH') {
    return null;
  }

  const details = getActionDetailObject(action) as
    | VulnerabilityPatchRemediationDetails
    | undefined;
  if (!details) {
    return null;
  }

  const fixablePackages = Array.isArray(details.fixablePackages)
    ? details.fixablePackages
        .map((entry) => normalizeRemediationPackage(entry))
        .filter((entry): entry is ActionPackageRow => entry !== null)
    : [];
  const unfixablePackages = Array.isArray(details.unfixablePackages)
    ? details.unfixablePackages
        .map((entry) => normalizeRemediationPackage(entry))
        .filter((entry): entry is ActionPackageRow => entry !== null)
    : [];

  if (fixablePackages.length === 0 && unfixablePackages.length === 0) {
    return null;
  }

  return {
    fixablePackages,
    unfixablePackages,
  };
}

function getActionTitle(action: VulnerabilityNextBestAction): string {
  return (
    normalizeText(action.title) ||
    toTitle(normalizeActionType(action.type).replace(/_/g, ' ')) ||
    'Recommended action'
  );
}

function getActionTypeLabel(
  action: VulnerabilityNextBestAction,
): string | undefined {
  const normalizedType = normalizeActionType(action.type);
  if (!DISPLAYABLE_NEXT_BEST_ACTION_TYPES.has(normalizedType)) {
    return undefined;
  }
  return toTitle(normalizedType.replace(/_/g, ' '));
}

function ActionTypeIcon({ type }: { type?: string }) {
  const normalizedType = normalizeActionType(type);

  if (normalizedType === 'HARDEN') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3l7 3v5c0 4.4-2.6 8.4-7 10-4.4-1.6-7-5.6-7-10V6l7-3z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (normalizedType === 'PATCH') {
    return <Wrench aria-hidden="true" strokeWidth={1.8} />;
  }

  if (normalizedType === 'MITIGATE') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M8 10V8a4 4 0 118 0v2M7 10h10a1 1 0 011 1v7a1 1 0 01-1 1H7a1 1 0 01-1-1v-7a1 1 0 011-1z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (normalizedType === 'EXEMPT') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M8 4h8l3 3v13H5V4h3zm8 0v4h4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return null;
}

function getActionGuideLabel(action: VulnerabilityNextBestAction): string {
  const normalizedType = normalizeActionType(action.type);

  if (normalizedType === 'HARDEN') {
    return 'Show hardening guide';
  }
  if (normalizedType === 'PATCH') {
    return 'Show patch guide';
  }
  if (normalizedType === 'MITIGATE') {
    return 'Show mitigation guide';
  }
  if (normalizedType === 'EXEMPT') {
    return 'Exempt';
  }

  return 'Show remediation guide';
}

function buildActionGuidePrompt(action: VulnerabilityNextBestAction): string {
  const normalizedType = normalizeActionType(action.type);

  if (normalizedType === 'EXEMPT') {
    return 'Exempt this finding. The exemption reason is Accepted Risk.';
  }

  return getActionGuideLabel(action);
}

function getRequirementLabel(
  evaluation: VulnerabilityInvestigationEvaluation,
): string {
  const requirement = evaluation.requirement || {};
  return (
    normalizeText(
      pickFirst(requirement.value, requirement.description, requirement.key),
    ) || 'this exploitation requirement'
  );
}

function buildActionGuideContextPrompt(
  action: VulnerabilityNextBestAction,
  options: {
    vulnerabilityId?: string;
    assetId?: string;
  },
): string {
  const normalizedType = normalizeActionType(action.type);
  const actionTitle = getActionTitle(action);
  const actionType =
    normalizedType === 'HARDEN'
      ? 'Hardening'
      : normalizedType === 'PATCH'
        ? 'Patch'
        : normalizedType === 'MITIGATE'
          ? 'Mitigation'
          : normalizedType === 'EXEMPT'
            ? 'Exemption'
            : 'Remediation';
  const lines = [
    `Remediation path: ${actionTitle}`,
    `Action type: ${actionType}`,
  ];

  if (options.vulnerabilityId) {
    lines.push(`Vulnerability: ${options.vulnerabilityId}`);
  }
  if (options.assetId) {
    lines.push(`Asset: ${options.assetId}`);
  }
  if (normalizeText(action.description)) {
    lines.push(`Summary: ${normalizeText(action.description)}`);
  }
  if (normalizeText(action.impact)) {
    lines.push(`Impact: ${normalizeText(action.impact)}`);
  }

  if (normalizedType === 'EXEMPT') {
    lines.push(
      'Task: Explain the residual risk, what information is required to record the exemption safely, the approval/owner details to capture, and when this exemption should be reviewed.',
    );
  } else {
    lines.push(
      'Task: Provide a step-by-step guide tailored to this remediation path. Include prerequisites, exact steps, validation checks, and follow-up actions.',
    );
  }

  return lines.join('\n');
}

function buildRequirementExplainContextPrompt(
  evaluation: VulnerabilityInvestigationEvaluation,
  options: {
    vulnerabilityId?: string;
    assetId?: string;
  },
): string {
  const requirement = evaluation.requirement || {};
  const requirementLabel = getRequirementLabel(evaluation);
  const normalizedStatus = normalizeStatus(evaluation.status);
  const lines = [
    `Requirement: ${requirementLabel}`,
    `Current status: ${STATUS_TAG_LABELS[normalizedStatus]}`,
  ];

  if (options.vulnerabilityId) {
    lines.push(`Vulnerability: ${options.vulnerabilityId}`);
  }
  if (options.assetId) {
    lines.push(`Asset: ${options.assetId}`);
  }
  if (typeof requirement.isHardStop === 'boolean') {
    lines.push(`Hard stop: ${requirement.isHardStop ? 'Yes' : 'No'}`);
  }

  const confidence = pickFirst(
    evaluation.evaluationConfidence,
    requirement.requirementConfidence,
    requirement.confidence,
  );
  if (confidence !== undefined && confidence !== null) {
    lines.push(`Confidence: ${formatPercent(confidence, 0)}`);
  }

  const reasoning = buildEvaluationReasoning(evaluation);
  if (reasoning) {
    lines.push(`Reasoning: ${reasoning}`);
  }

  const evidenceEntries = Array.isArray(evaluation.evidence)
    ? evaluation.evidence
        .map((entry) => normalizeText(entry))
        .filter((entry): entry is string => Boolean(entry))
    : [];
  if (evidenceEntries.length > 0) {
    lines.push(`Evidence: ${evidenceEntries.join(' | ')}`);
  }

  lines.push(
    'Task: Explain why this requirement has its current status and what that means in practical terms for exploitability.',
  );

  return lines.join('\n');
}

function buildRequirementQuestionContextPrompt(
  evaluation: VulnerabilityInvestigationEvaluation,
  options: {
    vulnerabilityId?: string;
    assetId?: string;
  },
): string {
  const requirement = evaluation.requirement || {};
  const requirementLabel = getRequirementLabel(evaluation);
  const normalizedStatus = normalizeStatus(evaluation.status);
  const lines = [
    `The user is asking about the exploitation requirement "${requirementLabel}".`,
    `Current status: ${STATUS_TAG_LABELS[normalizedStatus]}`,
  ];

  if (options.vulnerabilityId) {
    lines.push(`Vulnerability: ${options.vulnerabilityId}`);
  }
  if (options.assetId) {
    lines.push(`Asset: ${options.assetId}`);
  }
  if (typeof requirement.isHardStop === 'boolean') {
    lines.push(`Hard stop: ${requirement.isHardStop ? 'Yes' : 'No'}`);
  }

  const reasoning = buildEvaluationReasoning(evaluation);
  if (reasoning) {
    lines.push(`Reasoning: ${reasoning}`);
  }

  const evidenceEntries = Array.isArray(evaluation.evidence)
    ? evaluation.evidence
        .map((entry) => normalizeText(entry))
        .filter((entry): entry is string => Boolean(entry))
    : [];
  if (evidenceEntries.length > 0) {
    lines.push(`Evidence: ${evidenceEntries.join(' | ')}`);
  }

  lines.push(
    'Task: Answer the user using this investigation context. Be specific about this requirement and its impact on exploitability.',
  );

  return lines.join('\n');
}

function buildCreateTicketPrompt(
  vulnerabilityId: string | undefined,
  ticketingToolName: string,
): string {
  return `Create a ${ticketingToolName} ticket to track remediation of ${vulnerabilityId || 'this vulnerability'}.

Use ${ticketingToolName} for this request. Include the CVE details, affected asset, exploitability verdict, effective severity, and recommended remediation in the ticket. Ask me only the additional clarifying questions you need before creating the ticket. If the current context is already sufficient, create the ticket directly.`;
}

function buildCreateTicketContextPrompt(options: {
  vulnerabilityId?: string;
  assetId?: string;
  verdict?: string;
  effectiveSeverity?: string;
  topRemediation?: string;
  ticketingToolName?: string;
}): string {
  const lines = [`Vulnerability: ${options.vulnerabilityId || 'unknown'}`];
  if (options.assetId) {
    lines.push(`Asset: ${options.assetId}`);
  }
  if (options.verdict) {
    lines.push(`Exploitability verdict: ${options.verdict}`);
  }
  if (options.effectiveSeverity) {
    lines.push(`Effective severity: ${options.effectiveSeverity}`);
  }
  if (options.topRemediation) {
    lines.push(`Recommended remediation: ${options.topRemediation}`);
  }
  if (options.ticketingToolName) {
    lines.push(`Ticketing system: ${options.ticketingToolName}`);
  }
  return lines.join('\n');
}

function isNvdSource(rawSource: unknown): boolean {
  if (typeof rawSource !== 'string') {
    return false;
  }
  return rawSource.toLowerCase().includes('nvd');
}

function getVulnerabilityMetrics(
  vulnerability: VulnerabilityDetails,
): Record<string, unknown>[] {
  if (Array.isArray(vulnerability.metrics)) {
    return vulnerability.metrics.filter(isRecord);
  }
  if (isRecord(vulnerability.metrics)) {
    return [vulnerability.metrics];
  }
  return [];
}

function getMetricSource(metric: Record<string, unknown>): unknown {
  if (isRecord(metric.data) && metric.data.source !== undefined) {
    return metric.data.source;
  }
  return metric.source;
}

function getMetricVector(metric: Record<string, unknown>): string | undefined {
  if (isRecord(metric.data)) {
    const dataVector = normalizeText(
      pickFirst(
        metric.data.vector,
        metric.data.vectorString,
        metric.data.cvssVector,
      ),
    );
    if (dataVector) {
      return dataVector;
    }
  }
  return normalizeText(
    pickFirst(metric.vector, metric.vectorString, metric.cvssVector),
  );
}

function getMetricBaseScore(metric: Record<string, unknown>): number | null {
  if (isRecord(metric.data)) {
    const dataScore = parseScore(metric.data.baseScore);
    if (dataScore !== null) {
      return dataScore;
    }
  }
  return parseScore(metric.baseScore);
}

function extractNvdCvssMetric(
  vulnerability: VulnerabilityDetails,
): Record<string, unknown> | undefined {
  return getVulnerabilityMetrics(vulnerability).find((metric) => {
    const kind = normalizeText(metric.kind);
    if (kind && kind.toLowerCase() !== 'cvss') {
      return false;
    }
    return isNvdSource(getMetricSource(metric));
  });
}

function extractNvdCvssVector(
  vulnerability: VulnerabilityDetails,
): string | undefined {
  const metric = extractNvdCvssMetric(vulnerability);
  if (!metric) {
    return undefined;
  }
  return getMetricVector(metric);
}

function extractNvdCvssBaseScore(
  vulnerability: VulnerabilityDetails,
): number | null {
  const metric = extractNvdCvssMetric(vulnerability);
  if (!metric) {
    return null;
  }
  return getMetricBaseScore(metric);
}

function extractAnyCvssVector(
  vulnerability: VulnerabilityDetails,
): string | undefined {
  const metricVector = getVulnerabilityMetrics(vulnerability)
    .map((metric) => getMetricVector(metric))
    .find((vector): vector is string => Boolean(vector));
  if (metricVector) {
    return metricVector;
  }
  return normalizeText(
    pickFirst(
      vulnerability.cvssVector,
      vulnerability.cvss?.vector,
      isRecord(vulnerability.metrics)
        ? vulnerability.metrics.cvssVector
        : undefined,
    ),
  );
}

function extractAnyCvssBaseScore(
  vulnerability: VulnerabilityDetails,
): number | null {
  const metricScore = getVulnerabilityMetrics(vulnerability)
    .map((metric) => getMetricBaseScore(metric))
    .find((score): score is number => score !== null);
  if (metricScore !== undefined) {
    return metricScore;
  }
  return parseScore(
    pickFirst(
      vulnerability.cvssScore,
      vulnerability.cvss?.baseScore,
      vulnerability.cvss?.score,
      isRecord(vulnerability.metrics)
        ? vulnerability.metrics.cvssScore
        : undefined,
    ),
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeCweName(
  cweId: string | undefined,
  rawName: string | undefined,
): string | undefined {
  if (!rawName) {
    return undefined;
  }
  const normalizedName = rawName.trim();
  if (!cweId) {
    return normalizedName;
  }
  const idPrefixPattern = new RegExp(
    `^${escapeRegExp(cweId)}\\s*[:\\-]?\\s*`,
    'i',
  );
  const withoutIdPrefix = normalizedName.replace(idPrefixPattern, '').trim();
  return withoutIdPrefix || normalizedName;
}

function formatCwes(cwes: unknown): string | undefined {
  if (!Array.isArray(cwes) || cwes.length === 0) {
    return undefined;
  }
  const normalized = cwes
    .map((entry) => {
      if (typeof entry === 'string') {
        return normalizeText(entry);
      }
      if (!isRecord(entry)) {
        return undefined;
      }
      const cweId = normalizeText(pickFirst(entry.cweId, entry.id));
      const cweName = normalizeCweName(
        cweId,
        normalizeText(pickFirst(entry.name, entry.description)),
      );
      if (!cweId && !cweName) {
        return undefined;
      }
      if (!cweId) {
        return cweName;
      }
      return cweName ? `${cweId} (${cweName})` : cweId;
    })
    .filter((entry): entry is string => Boolean(entry));
  if (normalized.length === 0) {
    return undefined;
  }
  return normalized.join(', ');
}

function getVulnerabilityDefinition(
  report: VulnerabilityInvestigationData,
): VulnerabilityDefinition {
  return (report.vulnerabilityDefinition || {}) as VulnerabilityDefinition;
}

function getTopLevelCvssVector(
  report: VulnerabilityInvestigationData,
): string | undefined {
  if (!isRecord(report)) {
    return undefined;
  }
  return normalizeText(pickFirst(report.cvssVector, report.cvss?.vector));
}

function buildAssetTags(asset: VulnerabilityAsset | undefined): AssetTagRow[] {
  if (!asset || !Array.isArray(asset.resourceTags)) {
    return [];
  }

  return asset.resourceTags
    .map((tag: VulnerabilityInvestigationTag) => {
      const key = normalizeText(pickFirst(tag.Key, tag.key)) || '';
      const value = normalizeText(pickFirst(tag.Value, tag.value)) || '';
      if (!key && !value) {
        return null;
      }
      return {
        key: key || undefined,
        value: value || undefined,
      };
    })
    .filter((tag): tag is AssetTagRow => tag !== null);
}

function buildAssetRows(
  t: TFunction<'pleri/chat'>,
  report: VulnerabilityInvestigationData,
): DetailRow[] {
  const asset = report.asset || {};
  const vulnerability = report.vulnerability || {};
  const pkg = Array.isArray(vulnerability.packages)
    ? vulnerability.packages[0]
    : undefined;

  const assetNameTag = Array.isArray(asset.resourceTags)
    ? asset.resourceTags.find((tag) => tag.Key === 'Name' || tag.key === 'Name')
    : undefined;
  const assetName = assetNameTag
    ? pickFirst(assetNameTag.Value, assetNameTag.value)
    : pickFirst(asset.name, vulnerability.targetName, asset.resourceId);

  const targetPath = pkg?.targetPath || '';
  const osFromPath = targetPath.match(/\(([^)]+)\)/)?.[1] || '';
  const osLabel = pickFirst(asset.operatingSystem, asset.platform, '');
  const operatingSystem =
    osFromPath && osLabel
      ? `${osFromPath} (${osLabel})`
      : osFromPath || osLabel;

  const publicIp = Array.isArray(asset.publicIp)
    ? asset.publicIp[0]
    : asset.publicIp;

  const affectedPackage =
    pkg?.packageName && pkg?.installedVersion
      ? `${pkg.packageName} (${pkg.installedVersion})`
      : pickFirst(pkg?.packageName, pkg?.installedVersion);
  const assetUrl = pickFirst(report.assetUrl, asset.resourceURL);

  const rows: Array<{
    label: string;
    value: unknown;
    href?: string;
    linkLabel?: string;
  }> = [
    { label: t('assetName'), value: assetName },
    {
      label: t('resourceId'),
      value: pickFirst(asset.resourceId, vulnerability.targetName, asset.id),
    },
    {
      label: t('instanceType'),
      value: pickFirst(asset.instanceType, asset.machineType, asset.flavor),
    },
    { label: t('region'), value: pickFirst(asset.region) },
    { label: t('operatingSystem'), value: operatingSystem },
    { label: t('publicIp'), value: publicIp },
    { label: t('instanceState'), value: asset.instanceState },
    { label: t('affectedPackage'), value: affectedPackage },
    { label: t('fixedVersion'), value: pkg?.fixedVersion },
    {
      label: t('reference'),
      value: assetUrl,
      href: assetUrl,
      linkLabel: t('viewAsset'),
    },
  ];

  return rows
    .filter(
      (row) =>
        row.value !== null &&
        row.value !== undefined &&
        String(row.value).trim() !== '',
    )
    .map((row) => {
      const value = formatValue(row.value);
      const { href, linkLabel, ...rest } = row;
      return href ? { ...rest, value, href, linkLabel } : { ...rest, value };
    });
}

function buildCveRows(
  t: TFunction<'pleri/chat'>,
  report: VulnerabilityInvestigationData,
): DetailRow[] {
  const vulnerability = (report.vulnerability || {}) as VulnerabilityDetails;
  const finding = (report.vulnerabilityFinding || {}) as VulnerabilityDetails;
  const definition = getVulnerabilityDefinition(report);
  const cveId = pickFirst(
    report.cveId,
    vulnerability.vulnerabilityId,
    vulnerability.cveId,
    finding.vulnerabilityId,
    finding.cveId,
    definition.vulnerabilityId,
    definition.cveId,
  );
  const cvssVector =
    getTopLevelCvssVector(report) ||
    extractNvdCvssVector(vulnerability) ||
    extractNvdCvssVector(finding) ||
    extractNvdCvssVector(definition) ||
    extractAnyCvssVector(vulnerability) ||
    extractAnyCvssVector(finding) ||
    extractAnyCvssVector(definition);
  const severityLevel = pickFirst(
    vulnerability.baseSeverityLevel,
    vulnerability.baseSeverity?.level,
    vulnerability.severityLevel,
    finding.severityLevel,
    definition.baseSeverityLevel,
    definition.baseSeverity?.level,
    definition.severityLevel,
    isRecord(definition.severity) ? definition.severity.level : undefined,
  );
  const severitySource = pickFirst(
    vulnerability.severitySource,
    finding.severitySource,
    definition.severitySource,
    isRecord(definition.source) ? definition.source.name : undefined,
  );
  const severity = (() => {
    if (severityLevel && severitySource) {
      return `${toTitle(String(severityLevel))} (${severitySource})`;
    }
    if (severityLevel) {
      return toTitle(String(severityLevel));
    }
    if (severitySource) {
      return String(severitySource);
    }
    return undefined;
  })();

  const cweValue =
    formatCwes(vulnerability.cwes) ||
    formatCwes(finding.cwes) ||
    formatCwes(definition.cwes) ||
    formatCwes(definition.weaknesses) ||
    normalizeText(
      Array.isArray(vulnerability.cwe)
        ? vulnerability.cwe.join(', ')
        : vulnerability.cwe,
    ) ||
    normalizeText(
      Array.isArray(finding.cwe) ? finding.cwe.join(', ') : finding.cwe,
    ) ||
    normalizeText(
      Array.isArray(definition.cwe)
        ? definition.cwe.join(', ')
        : definition.cwe,
    ) ||
    normalizeText(
      pickFirst(vulnerability.cweId, finding.cweId, definition.cweId),
    );

  const title =
    pickFirst(vulnerability.title, finding.title, definition.title, '') || '';
  const titleParts = title ? title.split(/\s*:\s*/) : [];
  const affectedComponent =
    titleParts.length >= 2
      ? `Linux kernel ${titleParts[1].trim()} driver`
      : title
        ? `Linux kernel ${title
            .replace(/^kernel:\s*/i, '')
            .replace(/:.*$/, '')
            .trim()} driver`
        : undefined;
  const vulnerabilityType = title
    ? (titleParts.length >= 3
        ? titleParts.slice(2).join(': ')
        : title.replace(/^[^:]+:[^:]+:\s*/, '')
      )
        .replace(/^Fix\s+/i, '')
        .trim() || undefined
    : undefined;

  const hasKev = pickFirst(
    vulnerability.hasKev,
    finding.hasKev,
    definition.hasKev,
  );
  const kevStatus =
    typeof hasKev === 'boolean'
      ? hasKev
        ? t('inCisaKevCatalog')
        : t('notInCisaKevCatalog')
      : undefined;
  const hasExploit = pickFirst(
    vulnerability.hasExploit,
    finding.hasExploit,
    definition.hasExploit,
  );
  const knownExploits =
    typeof hasExploit === 'boolean'
      ? hasExploit
        ? t('detected')
        : t('noneDetected')
      : undefined;
  const hasVendorFix = pickFirst(
    vulnerability.hasVendorFix,
    finding.hasVendorFix,
    definition.hasVendorFix,
  );
  const vendorFixStatus =
    typeof hasVendorFix === 'boolean'
      ? hasVendorFix
        ? t('available')
        : t('notAvailable')
      : undefined;

  const nvdUrl = pickFirst(
    vulnerability.primaryUrl,
    finding.primaryUrl,
    definition.primaryUrl,
    cveId && `https://nvd.nist.gov/vuln/detail/${cveId}`,
  );

  const rows: Array<{
    label: string;
    value: unknown;
    href?: string;
    linkLabel?: string;
  }> = [
    { label: t('cveId'), value: cveId },
    { label: t('severity'), value: severity },
    { label: t('cvssVector'), value: cvssVector },
    {
      label: t('publishedDate'),
      value: pickFirst(
        vulnerability.publishedDate,
        vulnerability.publishedAt,
        finding.publishedDate,
        finding.publishedAt,
        definition.publishedDate,
        definition.publishedAt,
      ),
    },
    { label: 'CWE', value: cweValue },
    { label: t('affectedComponent'), value: affectedComponent },
    { label: t('vulnerabilityType'), value: vulnerabilityType },
    { label: t('vendorFix'), value: vendorFixStatus },
    { label: t('kevStatus'), value: kevStatus },
    { label: t('knownExploits'), value: knownExploits },
    {
      label: t('reference'),
      value: nvdUrl,
      href: nvdUrl,
      linkLabel: t('viewOnNvd'),
    },
  ];

  return rows
    .filter(
      (row) =>
        row.value !== null &&
        row.value !== undefined &&
        String(row.value).trim() !== '',
    )
    .map((row) => {
      const value =
        row.label === 'Published date'
          ? formatDate(String(row.value))
          : formatValue(row.value);
      const { href, linkLabel, ...rest } = row;
      return href ? { ...rest, value, href, linkLabel } : { ...rest, value };
    });
}

function summarizeStatusCounts(
  evaluations: VulnerabilityInvestigationEvaluation[],
) {
  const counts: Record<NormalizedStatus, number> = {
    MET: 0,
    NOT_MET: 0,
    UNKNOWN: 0,
  };
  evaluations.forEach((evaluation) => {
    counts[normalizeStatus(evaluation.status)] += 1;
  });
  return counts;
}

function DetailRows({
  rows,
  monoValues = false,
}: {
  rows: DetailRow[];
  monoValues?: boolean;
}) {
  return (
    <div className="detail-grid">
      {rows.map((row) => (
        <div key={`${row.label}-${row.value}`} className="detail-row">
          <span className="detail-label">{row.label}</span>
          <span className={monoValues ? 'detail-value mono' : 'detail-value'}>
            {row.href ? (
              <a
                href={row.href}
                target="_blank"
                rel="noreferrer"
                className="detail-link"
              >
                {row.linkLabel || row.value}
              </a>
            ) : (
              row.value
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function AssetTags({ tags }: { tags: AssetTagRow[] }) {
  const { t } = useTranslation('pleri/chat');
  const initialVisibleCount = 2;
  const pageSize = 4;
  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);

  if (tags.length === 0) {
    return null;
  }

  const visibleTags = tags.slice(0, visibleCount);
  const remainingCount = Math.max(tags.length - visibleTags.length, 0);
  const nextRevealCount = Math.min(pageSize, remainingCount);

  return (
    <div className="asset-tags">
      <p className="asset-tags-label">{t('tags')}</p>
      <div className="asset-tags-list">
        {visibleTags.map((tag, index) => (
          <div
            key={`${tag.key || 'tag'}-${tag.value || 'value'}-${index}`}
            className="asset-tag"
          >
            {tag.key && <span className="asset-tag-key">{tag.key}</span>}
            {tag.value && <span className="asset-tag-value">{tag.value}</span>}
          </div>
        ))}
      </div>
      {remainingCount > 0 && (
        <button
          type="button"
          className="asset-tags-show-more"
          onClick={() =>
            setVisibleCount((current) =>
              Math.min(current + pageSize, tags.length),
            )
          }
        >
          {t('showNextMore', { nextRevealCount })}
        </button>
      )}
    </div>
  );
}

export const VulnerabilityInvestigation: React.FC<
  VulnerabilityInvestigationProps
> = ({ data, className }) => {
  const { t } = useTranslation('pleri/chat');
  const report = data || {};
  const { enabledTicketingTools, isLoading: isLoadingTicketingTools } =
    useEnabledTicketingTools();
  const [activeFilter, setActiveFilter] = useState<EvaluationFilter>('ALL');
  const [activeActionIndex, setActiveActionIndex] = useState(0);
  const [showExploitabilityTooltip, setShowExploitabilityTooltip] =
    useState(false);
  const [openRequirementMenuId, setOpenRequirementMenuId] = useState<
    string | null
  >(null);
  const requirementMenuCloseTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const requirementAskInputRef = useRef<HTMLDivElement | null>(null);
  const ticketToolMenuRef = useRef<HTMLDivElement | null>(null);
  const [openRequirementAskInputId, setOpenRequirementAskInputId] = useState<
    string | null
  >(null);
  const [requirementAskInputValue, setRequirementAskInputValue] = useState('');
  const [isTicketToolMenuOpen, setIsTicketToolMenuOpen] = useState(false);

  const evaluations = useMemo(
    () => (Array.isArray(report.evaluations) ? report.evaluations : []),
    [report.evaluations],
  );
  const counts = useMemo(
    () => summarizeStatusCounts(evaluations),
    [evaluations],
  );

  const blockingFailures = useMemo(
    () =>
      evaluations.filter(
        (evaluation) =>
          evaluation.requirement?.isHardStop &&
          normalizeStatus(evaluation.status) === 'NOT_MET',
      ),
    [evaluations],
  );

  const filteredEvaluations = useMemo(() => {
    if (activeFilter === 'ALL') {
      return evaluations;
    }
    if (activeFilter === 'NOT_MET') {
      return evaluations.filter(
        (evaluation) => normalizeStatus(evaluation.status) === 'NOT_MET',
      );
    }
    if (activeFilter === 'MET') {
      return evaluations.filter(
        (evaluation) => normalizeStatus(evaluation.status) === 'MET',
      );
    }
    if (activeFilter === 'UNKNOWN') {
      return evaluations.filter(
        (evaluation) => normalizeStatus(evaluation.status) === 'UNKNOWN',
      );
    }
    return evaluations;
  }, [activeFilter, evaluations]);

  const remediations = useMemo(() => {
    const actions = Array.isArray(report.remediations)
      ? report.remediations
      : Array.isArray(report.nextBestActions)
        ? report.nextBestActions
        : Array.isArray(report.remediation)
          ? report.remediation
          : isRecord(report.remediation)
            ? [report.remediation]
            : [];

    return [...actions]
      .map((action, index) => ({
        action,
        index,
        priority: parseActionPriority(action.priority),
      }))
      .sort((left, right) => {
        if (left.priority === null && right.priority === null) {
          return left.index - right.index;
        }
        if (left.priority === null) {
          return 1;
        }
        if (right.priority === null) {
          return -1;
        }
        if (left.priority !== right.priority) {
          return left.priority - right.priority;
        }
        return left.index - right.index;
      })
      .map((entry) => entry.action);
  }, [report.nextBestActions, report.remediation, report.remediations]);
  const visibleActionIndex =
    remediations.length === 0
      ? null
      : activeActionIndex < remediations.length
        ? activeActionIndex
        : 0;
  const activeAction =
    visibleActionIndex === null ? null : remediations[visibleActionIndex];
  const singleTicketingTool =
    enabledTicketingTools.length === 1 ? enabledTicketingTools[0] : null;
  const createTicketButtonLabel = isLoadingTicketingTools
    ? t('checkingTools')
    : !enabledTicketingTools.length
      ? t('setUpTicketing')
      : singleTicketingTool
        ? t('createToolTicket', { name: singleTicketingTool.name })
        : t('createTicket');

  useEffect(() => {
    return () => {
      if (requirementMenuCloseTimeoutRef.current) {
        clearTimeout(requirementMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        requirementAskInputRef.current &&
        !requirementAskInputRef.current.contains(target)
      ) {
        setOpenRequirementAskInputId(null);
        setRequirementAskInputValue('');
      }

      if (
        ticketToolMenuRef.current &&
        !ticketToolMenuRef.current.contains(target)
      ) {
        setIsTicketToolMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (enabledTicketingTools.length <= 1) {
      setIsTicketToolMenuOpen(false);
    }
  }, [enabledTicketingTools.length]);

  const clearRequirementMenuCloseTimeout = () => {
    if (requirementMenuCloseTimeoutRef.current) {
      clearTimeout(requirementMenuCloseTimeoutRef.current);
      requirementMenuCloseTimeoutRef.current = null;
    }
  };

  const openRequirementMenu = (menuId: string) => {
    clearRequirementMenuCloseTimeout();
    setOpenRequirementMenuId(menuId);
  };

  const scheduleRequirementMenuClose = () => {
    clearRequirementMenuCloseTimeout();
    requirementMenuCloseTimeoutRef.current = setTimeout(() => {
      setOpenRequirementMenuId(null);
      requirementMenuCloseTimeoutRef.current = null;
    }, 150);
  };

  const closeRequirementMenuImmediately = () => {
    clearRequirementMenuCloseTimeout();
    setOpenRequirementMenuId(null);
  };

  const closeRequirementAskInput = () => {
    setOpenRequirementAskInputId(null);
    setRequirementAskInputValue('');
  };

  const vulnerability = (report.vulnerability ||
    report.vulnerabilityFinding ||
    report.vulnerabilityDefinition ||
    {}) as VulnerabilityDetails;
  const finding = (report.vulnerabilityFinding || {}) as VulnerabilityDetails;
  const definition = getVulnerabilityDefinition(report);
  const normalizedVerdict = normalizeVerdict(report.verdict);
  const baselineScore =
    parseScore(
      pickFirst(
        vulnerability.baseSeverity?.score,
        report.baseSeverity?.score,
        extractNvdCvssBaseScore(vulnerability),
        extractNvdCvssBaseScore(finding),
        extractNvdCvssBaseScore(definition),
        extractAnyCvssBaseScore(vulnerability),
        extractAnyCvssBaseScore(finding),
        extractAnyCvssBaseScore(definition),
        isRecord(definition.severity) ? definition.severity.score : undefined,
      ),
    ) ??
    severityLevelToScore(
      pickFirst(
        vulnerability.baseSeverityLevel,
        vulnerability.baseSeverity?.level,
        vulnerability.severityLevel,
        definition.baseSeverityLevel,
        definition.baseSeverity?.level,
        definition.severityLevel,
        normalizeText(
          isRecord(definition.severity) ? definition.severity.level : undefined,
        ),
      ),
    );

  const baselineSeverity =
    toTitle(
      pickFirst(
        vulnerability.baseSeverityLevel,
        vulnerability.baseSeverity?.level,
        report.baseSeverity?.level,
        vulnerability.severityLevel,
        definition.baseSeverityLevel,
        definition.baseSeverity?.level,
        definition.severityLevel,
        normalizeText(
          isRecord(definition.severity) ? definition.severity.level : undefined,
        ),
        scoreToSeverity(baselineScore),
      ),
    ) || t('unknownNoun');
  const operationalLevelFromPayload = toTitle(
    pickFirst(
      vulnerability.operationalSeverityLevel,
      vulnerability.operationalSeverity?.level,
      report.operationalSeverity?.level,
    ),
  );
  const operationalScoreFromPayload = parseScore(
    pickFirst(
      report.operationalSeverity?.score,
      vulnerability.operationalSeverity?.score,
    ),
  );
  const effectiveSeverity =
    operationalLevelFromPayload ||
    verdictToSeverity(normalizedVerdict, baselineScore);
  const effectiveScore =
    operationalScoreFromPayload ?? severityLabelToScore(effectiveSeverity);
  const headerSeverityClass = `header-severity-${String(effectiveSeverity || '')
    .toLowerCase()
    .replace(/\s+/g, '-')}`;

  const cveRows = buildCveRows(t, report);
  const assetRows = buildAssetRows(t, report);
  const assetTags = useMemo(() => buildAssetTags(report.asset), [report.asset]);

  const failingRequirement =
    pickFirst(
      blockingFailures[0]?.requirement?.value,
      blockingFailures[0]?.requirement?.reasoning,
    ) || t('noFailingRequirementRecorded');

  const verdictLabel =
    VERDICT_LABELS[normalizedVerdict] || toTitle(normalizedVerdict);
  const exploitabilityTooltipText =
    VERDICT_TOOLTIPS[normalizedVerdict] || VERDICT_TOOLTIPS.UNKNOWN;

  const narrativeSummary = (() => {
    if (normalizedVerdict === 'NOT_EXPLOITABLE') {
      return (
        <>
          <Trans
            t={t}
            i18nKey="thisAssetIsNotCurrentlyExploitableFor"
            values={{ failingRequirement }}
            components={{ strong: <strong /> }}
          />
        </>
      );
    }
    if (normalizedVerdict === 'LIKELY_NOT_EXPLOITABLE') {
      return (
        <>
          <Trans
            t={t}
            i18nKey="thisAssetIsLikelyNotExploitableFor"
            components={{ strong: <strong /> }}
          />
        </>
      );
    }
    if (normalizedVerdict === 'EXPLOITABLE') {
      return (
        <>
          <Trans
            t={t}
            i18nKey="thisAssetIsExploitableForThisCve"
            components={{ strong: <strong /> }}
          />
        </>
      );
    }
    if (normalizedVerdict === 'LIKELY_EXPLOITABLE') {
      return (
        <>
          <Trans
            t={t}
            i18nKey="thisAssetIsLikelyExploitableForThis"
            components={{ strong: <strong /> }}
          />
        </>
      );
    }
    return (
      <>
        <Trans
          t={t}
          i18nKey="exploitabilityCouldNotBeDeterminedOneOr"
          components={{ strong: <strong /> }}
        />
      </>
    );
  })();
  const baselineScoreProgress = scoreToProgressPercent(baselineScore);
  const operationalScoreProgress = scoreToProgressPercent(effectiveScore);
  const handleRemediationGuide = () => {
    if (!activeAction) {
      return;
    }

    const vulnerabilityId = pickFirst(
      report.cveId,
      vulnerability.vulnerabilityId,
      vulnerability.cveId,
    );
    const assetId = normalizeText(report.assetId);
    const actionTitle = getActionTitle(activeAction);
    const contextItem: ContextItem = {
      id: `remediation-${vulnerabilityId}-${Date.now()}`,
      type: 'vulnerability_investigation',
      prompt: buildActionGuideContextPrompt(activeAction, {
        vulnerabilityId,
        assetId,
      }),
      display: actionTitle || vulnerabilityId || 'Remediation',
    };

    PleriClient.updateChatInput({
      prompt: buildActionGuidePrompt(activeAction),
      context: [contextItem],
      useExistingConversation: true,
      autoSubmit: true,
    });
  };
  const navigateToTicketingSetup = () => {
    PleriClient.navigate({
      to: '/settings/tools',
      search: {
        category: 'Ticketing',
        source: 'vulnerability-investigation-ticket-cta',
      },
    });
  };

  const handleCreateTicket = (
    ticketingTool: EnabledTicketingTool,
    remediationAction?: VulnerabilityNextBestAction | null,
  ) => {
    setIsTicketToolMenuOpen(false);

    const vulnerabilityId = pickFirst(
      report.cveId,
      vulnerability.vulnerabilityId,
      vulnerability.cveId,
    );
    const assetId = normalizeText(report.assetId);
    const selectedRemediation = remediationAction
      ? getActionTitle(remediationAction)
      : remediations.length > 0
        ? getActionTitle(remediations[0])
        : undefined;
    const contextItem: ContextItem = {
      id: `create-ticket-${vulnerabilityId}-${Date.now()}`,
      type: 'vulnerability_investigation',
      prompt: buildCreateTicketContextPrompt({
        vulnerabilityId,
        assetId,
        verdict: verdictLabel,
        effectiveSeverity,
        topRemediation: selectedRemediation,
        ticketingToolName: ticketingTool.name,
      }),
      display: vulnerabilityId || 'Vulnerability',
    };

    PleriClient.updateChatInput({
      prompt: buildCreateTicketPrompt(vulnerabilityId, ticketingTool.name),
      context: [contextItem],
      useExistingConversation: true,
      autoSubmit: true,
      selectedToolGroups: [ticketingTool.id],
    });
  };

  const handleCreateTicketCtaClick = (
    remediationAction?: VulnerabilityNextBestAction | null,
  ) => {
    if (isLoadingTicketingTools) {
      return;
    }

    if (!enabledTicketingTools.length) {
      navigateToTicketingSetup();
      return;
    }

    if (singleTicketingTool) {
      handleCreateTicket(singleTicketingTool, remediationAction);
      return;
    }

    setIsTicketToolMenuOpen((currentValue) => !currentValue);
  };

  const handleRequirementExplain = (
    evaluation: VulnerabilityInvestigationEvaluation,
  ) => {
    const vulnerabilityId = pickFirst(
      report.cveId,
      vulnerability.vulnerabilityId,
      vulnerability.cveId,
    );
    const assetId = normalizeText(report.assetId);
    const requirementLabel = getRequirementLabel(evaluation);
    const contextItem: ContextItem = {
      id: `explain-requirement-${requirementLabel}-${Date.now()}`,
      type: 'vulnerability_investigation',
      prompt: buildRequirementExplainContextPrompt(evaluation, {
        vulnerabilityId,
        assetId,
      }),
      display: requirementLabel,
    };

    PleriClient.updateChatInput({
      prompt: 'Explain this exploitation requirement',
      context: [contextItem],
      useExistingConversation: true,
      autoSubmit: true,
    });
  };
  const handleRequirementAskPleri = (
    requirementMenuId: string,
    _evaluation: VulnerabilityInvestigationEvaluation,
  ) => {
    closeRequirementMenuImmediately();
    setOpenRequirementAskInputId(requirementMenuId);
    setRequirementAskInputValue('');
  };

  const handleRequirementAskSubmit = (
    evaluation: VulnerabilityInvestigationEvaluation,
  ) => {
    const userQuestion = requirementAskInputValue.trim();
    if (!userQuestion) {
      return;
    }

    const vulnerabilityId = pickFirst(
      report.cveId,
      vulnerability.vulnerabilityId,
      vulnerability.cveId,
    );
    const assetId = normalizeText(report.assetId);
    const requirementLabel = getRequirementLabel(evaluation);
    const contextItem: ContextItem = {
      id: `ask-requirement-${requirementLabel}-${Date.now()}`,
      type: 'vulnerability_investigation',
      prompt: buildRequirementQuestionContextPrompt(evaluation, {
        vulnerabilityId,
        assetId,
      }),
      display: requirementLabel,
    };

    PleriClient.updateChatInput({
      prompt: userQuestion,
      context: [contextItem],
      useExistingConversation: true,
      autoSubmit: true,
    });

    closeRequirementAskInput();
  };

  return (
    <div className={cn('vi-report-ui', className)}>
      <div className="app-shell">
        <div className="backdrop-pattern" />
        <main className="report">
          <header className={`header-card ${headerSeverityClass}`}>
            <div className="header-top">
              <div>
                <p className="eyebrow">{report.cveId || t('unknownCve')}</p>
                <h1 className="vi-vulnerability-summary-title">
                  {report.vulnerabilitySummary ||
                    vulnerability.title ||
                    t('contextualExploitabilityReview', {
                      cveId: report.cveId || t('unknownCve'),
                    })}
                </h1>
                {report.assessedAt && (
                  <p className="header-assessment-date">
                    {t('assessedAt', { date: formatDate(report.assessedAt) })}
                  </p>
                )}
              </div>
            </div>
          </header>

          <section className="section-card">
            <div className="section-heading">
              <h2>{t('assessment')}</h2>
            </div>
            <div className="severity-row">
              <article
                className={`severity-box ${severityTone(baselineSeverity)}`}
              >
                <div className="severity-meta">
                  <span className="severity-caption">{t('baseSeverity')}</span>
                  <span className="severity-pill">{baselineSeverity}</span>
                </div>
                <div className="severity-score-row">
                  <span className="severity-score">
                    {formatScore(baselineScore)}
                  </span>
                  <span className="severity-score-unit">{t('score')}</span>
                </div>
                <div className="severity-meter" aria-hidden>
                  <span
                    className="severity-meter-fill"
                    style={{ width: `${baselineScoreProgress}%` }}
                  />
                </div>
              </article>
              <div className="severity-arrow">→</div>
              <article
                className={`severity-box ${severityTone(effectiveSeverity)}`}
              >
                <div className="severity-meta">
                  <span className="severity-caption">
                    {t('operationalSeverity')}
                  </span>
                  <span className="severity-pill">{effectiveSeverity}</span>
                </div>
                <div className="severity-score-row">
                  <span className="severity-score">
                    {formatScore(effectiveScore)}
                  </span>
                  <span className="severity-score-unit">{t('score')}</span>
                </div>
                <div className="severity-meter" aria-hidden>
                  <span
                    className="severity-meter-fill"
                    style={{ width: `${operationalScoreProgress}%` }}
                  />
                </div>
              </article>
            </div>
            <div className="exploitability-row">
              <span className="exploitability-label">
                {t('exploitability')}
              </span>
              <span
                className={`exploitability-pill exploitability-${String(
                  normalizedVerdict || 'UNKNOWN',
                ).toLowerCase()}`}
              >
                {verdictLabel}
              </span>
              <div
                className="exploitability-tooltip-wrap"
                onMouseEnter={() => setShowExploitabilityTooltip(true)}
                onMouseLeave={() => setShowExploitabilityTooltip(false)}
              >
                <button
                  type="button"
                  className="exploitability-help-btn"
                  aria-label={t('showExploitabilityVerdictDetails')}
                >
                  ?
                </button>
                {showExploitabilityTooltip && (
                  <div className="exploitability-tooltip" role="tooltip">
                    <p className="exploitability-tooltip-text">
                      {exploitabilityTooltipText}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <p className="narrative">{narrativeSummary}</p>
          </section>

          <div className="content-layout">
            <div className="primary-column">
              <section className="section-card">
                <div className="section-heading">
                  <h2>{t('exploitationRequirements')}</h2>
                </div>

                <div className="filter-row">
                  <button
                    type="button"
                    className={
                      activeFilter === 'ALL'
                        ? 'filter-btn active'
                        : 'filter-btn'
                    }
                    onClick={() => setActiveFilter('ALL')}
                  >
                    {t('allWithCount', { length: evaluations.length })}
                  </button>
                  <button
                    type="button"
                    className={
                      activeFilter === 'NOT_MET'
                        ? 'filter-btn active'
                        : 'filter-btn'
                    }
                    onClick={() => setActiveFilter('NOT_MET')}
                  >
                    {t('notMet', { NOT_MET: counts.NOT_MET })}
                  </button>
                  <button
                    type="button"
                    className={
                      activeFilter === 'MET'
                        ? 'filter-btn active'
                        : 'filter-btn'
                    }
                    onClick={() => setActiveFilter('MET')}
                  >
                    {t('met', { MET: counts.MET })}
                  </button>
                  <button
                    type="button"
                    className={
                      activeFilter === 'UNKNOWN'
                        ? 'filter-btn active'
                        : 'filter-btn'
                    }
                    onClick={() => setActiveFilter('UNKNOWN')}
                  >
                    {t('unknown', { UNKNOWN: counts.UNKNOWN })}
                  </button>
                </div>

                <div className="evaluation-list">
                  {filteredEvaluations.length === 0 && (
                    <div className="empty-state">
                      {t('noEvaluationsAvailableForThisFilter')}
                    </div>
                  )}
                  {filteredEvaluations.map((evaluation, index) => {
                    const requirement = evaluation.requirement || {};
                    const requirementMenuId = `${requirement.key || requirement.value || 'req'}-${index}`;
                    const status = normalizeStatus(evaluation.status);
                    const statusClass = STATUS_CLASS[status];
                    const confidence = pickFirst(
                      evaluation.evaluationConfidence,
                      requirement.requirementConfidence,
                      requirement.confidence,
                    );
                    const reasoning = buildEvaluationReasoning(evaluation);

                    return (
                      <article
                        key={`${requirement.key || requirement.value || 'req'}-${index}`}
                        className={`evaluation-card ${statusClass}`}
                      >
                        <div className="evaluation-head">
                          <span className={`evaluation-status ${statusClass}`}>
                            {STATUS_TAG_LABELS[status]}
                          </span>
                          <div
                            className={cn(
                              'requirement-pleri-actions',
                              (openRequirementMenuId === requirementMenuId ||
                                openRequirementAskInputId ===
                                  requirementMenuId) &&
                                'is-open',
                            )}
                            onMouseEnter={() =>
                              openRequirementMenu(requirementMenuId)
                            }
                            onMouseLeave={scheduleRequirementMenuClose}
                            onFocus={() =>
                              openRequirementMenu(requirementMenuId)
                            }
                            onBlur={(event) => {
                              const nextFocused = event.relatedTarget;
                              if (
                                nextFocused instanceof Node &&
                                event.currentTarget.contains(nextFocused)
                              ) {
                                return;
                              }
                              closeRequirementMenuImmediately();
                              closeRequirementAskInput();
                            }}
                          >
                            <button
                              type="button"
                              className="requirement-pleri-trigger"
                              aria-label={t(
                                'openPleriActionsForThisRequirement',
                              )}
                              aria-haspopup="menu"
                              aria-expanded={
                                openRequirementMenuId === requirementMenuId ||
                                openRequirementAskInputId === requirementMenuId
                              }
                            >
                              <SparklesIcon className="requirement-pleri-trigger-icon" />
                            </button>
                            {openRequirementAskInputId === requirementMenuId ? (
                              <div
                                ref={requirementAskInputRef}
                                className="requirement-pleri-input"
                              >
                                <ChatBubbleLeftEllipsisIcon className="requirement-pleri-input-icon" />
                                <input
                                  type="text"
                                  value={requirementAskInputValue}
                                  onChange={(event) =>
                                    setRequirementAskInputValue(
                                      event.target.value,
                                    )
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === 'Enter' &&
                                      !event.shiftKey
                                    ) {
                                      event.preventDefault();
                                      handleRequirementAskSubmit(evaluation);
                                    } else if (event.key === 'Escape') {
                                      closeRequirementAskInput();
                                    }
                                  }}
                                  placeholder={t('askAboutThisRequirement')}
                                  className="requirement-pleri-input-field"
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  className="requirement-pleri-input-submit"
                                  disabled={!requirementAskInputValue.trim()}
                                  onClick={() =>
                                    handleRequirementAskSubmit(evaluation)
                                  }
                                >
                                  <ArrowRightIcon className="requirement-pleri-input-submit-icon" />
                                </button>
                              </div>
                            ) : (
                              <div
                                className="requirement-pleri-menu"
                                role="menu"
                                aria-label={t('requirementActions')}
                              >
                                <button
                                  type="button"
                                  className="requirement-pleri-option"
                                  role="menuitem"
                                  onClick={() => {
                                    closeRequirementMenuImmediately();
                                    handleRequirementExplain(evaluation);
                                  }}
                                >
                                  <InformationCircleIcon className="requirement-pleri-option-icon" />
                                  {t('explain')}
                                </button>
                                <button
                                  type="button"
                                  className="requirement-pleri-option"
                                  role="menuitem"
                                  onClick={() =>
                                    handleRequirementAskPleri(
                                      requirementMenuId,
                                      evaluation,
                                    )
                                  }
                                >
                                  <ChatBubbleLeftEllipsisIcon className="requirement-pleri-option-icon" />
                                  {t('askPleri')}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="requirement-statement">
                          <span className="requirement-statement-label">
                            {t('requirement')}{' '}
                          </span>
                          <span className="requirement-statement-text">
                            {requirement.value || t('unlabeledRequirement')}
                          </span>
                        </p>
                        {reasoning && <p className="reasoning">{reasoning}</p>}
                        <div className="requirement-meta">
                          <span className="requirement-confidence-text">
                            {confidence === null || confidence === undefined
                              ? t('confidenceUnknown')
                              : `Confidence: ${formatPercent(confidence, 0)}`}
                          </span>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            </div>

            <aside className="sidebar">
              <section className="section-card sidebar-card">
                <div className="section-heading compact">
                  <h2>{t('affectedAsset')}</h2>
                </div>
                <DetailRows rows={assetRows} monoValues={true} />
                <AssetTags tags={assetTags} />
              </section>

              <section className="section-card sidebar-card">
                <div className="section-heading compact">
                  <h2>{t('cveDetails')}</h2>
                </div>
                <DetailRows rows={cveRows} />
              </section>
            </aside>
          </div>

          {activeAction && (
            <section className="section-card action-workbench">
              <div className="section-heading">
                <h2>{t('remediation')}</h2>
              </div>

              <div className="action-layout">
                <div
                  className="action-list"
                  role="tablist"
                  aria-label={t('remediation')}
                >
                  {remediations.map((action, index) => {
                    const isActive = visibleActionIndex === index;
                    const actionNavLabel =
                      getActionTypeLabel(action) || getActionTitle(action);

                    return (
                      <button
                        key={`${getActionTitle(action)}-${index}`}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={cn('action-nav-item', isActive && 'active')}
                        onClick={() => setActiveActionIndex(index)}
                      >
                        <span className="action-nav-label">
                          <span className="action-nav-icon">
                            <ActionTypeIcon type={action.type} />
                          </span>
                          <span className="action-nav-title">
                            {actionNavLabel}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="action-pane" role="tabpanel">
                  <div className="action-pane-header">
                    <h3 className="action-pane-title">
                      {getActionTitle(activeAction)}
                    </h3>
                    {getActionNarrative(activeAction) && (
                      <p className="action-pane-summary">
                        {getActionNarrative(activeAction)}
                      </p>
                    )}
                  </div>

                  <div className="action-pane-meta">
                    {(() => {
                      const impact = getActionImpact(activeAction);
                      const patchPackageGroups =
                        getPatchPackageGroups(activeAction);
                      const details = buildActionDetails(activeAction);

                      return (
                        <>
                          {impact && (
                            <div className="action-pane-card">
                              <span className="action-pane-card-label">
                                {t('impact')}
                              </span>
                              <p className="action-pane-card-value">{impact}</p>
                            </div>
                          )}
                          {patchPackageGroups?.fixablePackages.length ? (
                            <div className="action-pane-card action-pane-card-full">
                              <span className="action-pane-card-label">
                                {t('fixablePackages')}
                              </span>
                              <div className="action-package-list">
                                {patchPackageGroups.fixablePackages.map(
                                  (pkg, index) => (
                                    <div
                                      key={`${pkg.packageName}-${pkg.installedVersion || 'current'}-${index}`}
                                      className="action-package-row action-package-row-fixable"
                                    >
                                      <div className="action-package-main">
                                        <p className="action-package-name">
                                          {pkg.packageName}
                                        </p>
                                        {pkg.target && (
                                          <p className="action-package-target">
                                            {pkg.target}
                                          </p>
                                        )}
                                      </div>
                                      <div className="action-package-upgrade-flow">
                                        {(() => {
                                          const hasUpgradePath = Boolean(
                                            pkg.installedVersion &&
                                            pkg.fixedVersion,
                                          );

                                          return (
                                            <div
                                              className={cn(
                                                'action-package-upgrade-grid',
                                                hasUpgradePath && 'has-upgrade',
                                              )}
                                            >
                                              {pkg.installedVersion && (
                                                <>
                                                  <span className="action-package-version-heading action-package-version-heading-current">
                                                    {t('current')}
                                                  </span>
                                                  <span className="action-package-chip action-package-chip-current">
                                                    {pkg.installedVersion}
                                                  </span>
                                                </>
                                              )}
                                              {pkg.fixedVersion && (
                                                <>
                                                  <span className="action-package-version-heading action-package-version-heading-upgrade">
                                                    {t('upgradeTo')}
                                                  </span>
                                                  {hasUpgradePath && (
                                                    <span
                                                      className="action-package-arrow"
                                                      aria-hidden="true"
                                                    >
                                                      →
                                                    </span>
                                                  )}
                                                  <span className="action-package-chip action-package-chip-emphasis action-package-chip-upgrade">
                                                    {pkg.fixedVersion}
                                                  </span>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          ) : null}
                          {patchPackageGroups?.unfixablePackages.length ? (
                            <div className="action-pane-card action-pane-card-full">
                              <span className="action-pane-card-label">
                                {t('noFixAvailable')}
                              </span>
                              <div className="action-package-list">
                                {patchPackageGroups.unfixablePackages.map(
                                  (pkg, index) => (
                                    <div
                                      key={`${pkg.packageName}-${pkg.installedVersion || 'installed'}-${index}`}
                                      className="action-package-row"
                                    >
                                      <div className="action-package-main">
                                        <p className="action-package-name">
                                          {pkg.packageName}
                                        </p>
                                        {pkg.target && (
                                          <p className="action-package-target">
                                            {pkg.target}
                                          </p>
                                        )}
                                      </div>
                                      {pkg.installedVersion && (
                                        <div className="action-package-versions">
                                          <div className="action-package-version-block">
                                            <span className="action-package-version-label">
                                              {t('installedVersion')}
                                            </span>
                                            <span className="action-package-chip">
                                              {pkg.installedVersion}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          ) : null}
                          {details.length > 0 && (
                            <div className="action-pane-card action-pane-card-full">
                              <span className="action-pane-card-label">
                                {t('common:details')}
                              </span>
                              <div className="action-detail-list">
                                {details.map((detail) => (
                                  <div
                                    key={`${detail.label}-${detail.value}`}
                                    className="action-detail-row"
                                  >
                                    <span className="action-detail-label">
                                      {detail.label}
                                    </span>
                                    <span className="action-detail-value">
                                      {detail.value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="action-pane-footer">
                    <button
                      type="button"
                      className="action-pane-cta"
                      onClick={handleRemediationGuide}
                    >
                      <span>{getActionGuideLabel(activeAction)}</span>
                      <span
                        className="action-pane-cta-arrow"
                        aria-hidden="true"
                      >
                        →
                      </span>
                    </button>

                    <div
                      ref={ticketToolMenuRef}
                      className="create-ticket-menu-wrap"
                    >
                      <button
                        type="button"
                        className={cn(
                          'create-ticket-primary-btn',
                          enabledTicketingTools.length > 1 &&
                            'create-ticket-primary-btn-with-chevron',
                        )}
                        onClick={() => handleCreateTicketCtaClick(activeAction)}
                        disabled={isLoadingTicketingTools}
                        aria-haspopup={
                          enabledTicketingTools.length > 1 ? 'menu' : undefined
                        }
                        aria-expanded={
                          enabledTicketingTools.length > 1
                            ? isTicketToolMenuOpen
                            : undefined
                        }
                        aria-controls={
                          enabledTicketingTools.length > 1
                            ? 'vi-ticket-tool-menu'
                            : undefined
                        }
                      >
                        <span>{createTicketButtonLabel}</span>
                        {enabledTicketingTools.length > 1 && (
                          <ChevronDownIcon
                            className={cn(
                              'create-ticket-primary-btn-chevron',
                              isTicketToolMenuOpen &&
                                'create-ticket-primary-btn-chevron-open',
                            )}
                            aria-hidden="true"
                          />
                        )}
                      </button>

                      {enabledTicketingTools.length > 1 &&
                        isTicketToolMenuOpen && (
                          <div
                            id="vi-ticket-tool-menu"
                            className="create-ticket-tool-menu"
                            role="menu"
                            aria-label={t('availableTicketingTools')}
                          >
                            {enabledTicketingTools.map((ticketingTool) => (
                              <button
                                key={ticketingTool.id}
                                type="button"
                                className="create-ticket-tool-option"
                                onClick={() =>
                                  handleCreateTicket(
                                    ticketingTool,
                                    activeAction,
                                  )
                                }
                                role="menuitem"
                              >
                                {ticketingTool.name}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default VulnerabilityInvestigation;
